import { Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/LangContext";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useNavigate } from "react-router-dom";

export default function NamingRights() {
  const { t } = useLang();
  const navigate = useNavigate();

  const choose = (idx) => {
    if (idx === 0) return navigate("/subasta");
    window.dispatchEvent(
      new CustomEvent("montalban:set-interest", { detail: idx })
    );
    scrollToId("contacto");
  };

  return (
    <section
      id="naming"
      data-testid="naming-rights-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter number={t.naming.chapter} label={t.naming.label} />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-3xl font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
            {t.naming.title}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {t.naming.subtitle}
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {t.naming.packages.map((pkg, i) => {
            const featured = i === 0;
            return (
              <Reveal key={pkg.testid} delay={i * 0.08} className="h-full">
                <article
                  data-testid={pkg.testid}
                  className={`flex h-full flex-col border p-7 transition-colors duration-500 sm:p-9 ${
                    featured
                      ? "border-amber-400/60 bg-gradient-to-br from-amber-400/[0.08] to-transparent"
                      : "border-white/10 bg-[#121620] hover:border-amber-400/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span
                      className={`px-3 py-1 font-code text-[10px] uppercase tracking-[0.25em] ${
                        featured
                          ? "bg-red-500/15 text-red-400 border border-red-500/30"
                          : "border border-white/15 text-slate-400"
                      }`}
                    >
                      {pkg.badge}
                    </span>
                    <span className="font-display text-4xl font-bold text-white/15">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-white sm:text-3xl">
                    {pkg.title}
                  </h3>
                  <p className="mt-2 font-code text-xs uppercase tracking-[0.25em] text-amber-400">
                    {pkg.framing}
                  </p>
                  <ul className="mt-7 flex-1 space-y-3.5">
                    {pkg.highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <Check
                          size={16}
                          className="mt-0.5 shrink-0 text-amber-400"
                        />
                        <span className="text-sm leading-relaxed text-slate-300">
                          {h}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`${pkg.testid}-cta`}
                    onClick={() => choose(i)}
                    className={`group mt-8 flex items-center justify-between px-5 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors ${
                      featured
                        ? "bg-amber-400 text-[#0A0C10] hover:bg-amber-300"
                        : "border border-white/20 text-white hover:border-amber-400 hover:text-amber-400"
                    }`}
                  >
                    {pkg.cta}
                    <ArrowUpRight
                      size={17}
                      className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </button>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <p
            data-testid="naming-closing-note"
            className="mt-12 text-center font-code text-xs uppercase leading-relaxed tracking-[0.25em] text-slate-500 sm:text-sm"
          >
            {t.naming.closing}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
